import { useEffect, useState, useCallback } from "react";

export type Role = "viewer" | "editor" | "admin";

export interface AuthUser {
  email: string;
  name: string;
  groups: string[];
  role: Role;
}

interface AuthState {
  loading: boolean;
  user: AuthUser | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ loading: true, user: null });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await fetch("/auth/me", { credentials: "same-origin" });
      if (r.ok) {
        const u = (await r.json()) as AuthUser;
        setState({ loading: false, user: u });
      } else {
        setState({ loading: false, user: null });
      }
    } catch {
      setState({ loading: false, user: null });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(() => {
    const ret = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/auth/login?returnTo=${ret}`;
  }, []);

  const logout = useCallback(async () => {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
    setState({ loading: false, user: null });
    window.location.href = "/";
  }, []);

  function has(min: Role): boolean {
    if (!state.user) return false;
    const order: Role[] = ["viewer", "editor", "admin"];
    return order.indexOf(state.user.role) >= order.indexOf(min);
  }

  return { ...state, login, logout, refresh, has };
}
